import { create } from 'zustand';
import { http } from '@/libs/http';

export interface RoleAssignment {
    id: string;
    principal_id: string;
    principal_type: 'user' | 'group' | 'spn';
    role_id: string;
    scope_id: string;
    assigned_at?: string;
    assigned_by?: string;
    expires_at?: string;
}

export interface PermissionAssignment {
    id: string;
    principal_id: string;
    principal_type: 'user' | 'group' | 'spn';
    permission_id: string;
    scope_id: string;
    assigned_at?: string;
    assigned_by?: string;
    expires_at?: string;
}

export interface BulkAssignmentItem {
    role_id: string;
    scope_id: string;
    principal_type: 'user' | 'group' | 'spn';
    principal_id: string;
    expires_at?: string;
}

export interface BulkAssignmentResult {
    succeeded: RoleAssignment[];
    failed: Array<{ item: Omit<BulkAssignmentItem, 'expires_at'>; error: string }>;
    total: number;
    success_count: number;
    failure_count: number;
}

type AssignmentStore = {
    // Role assignments
    assignments: Record<string, RoleAssignment[]>;
    userAssignments: Record<string, RoleAssignment[]>;
    // Permission assignments
    permissionAssignments: Record<string, PermissionAssignment[]>;

    isLoading: boolean;
    error: string | null;

    // Role assignment actions
    list: (scopeId: string) => Promise<RoleAssignment[]>;
    listForPrincipal: (principalId: string, principalType: 'user' | 'group' | 'spn') => Promise<RoleAssignment[]>;
    create: (data: Omit<RoleAssignment, 'id' | 'assigned_at' | 'assigned_by'>) => Promise<RoleAssignment>;
    createBulk: (assignments: BulkAssignmentItem[]) => Promise<BulkAssignmentResult>;
    delete: (assignmentId: string, scopeId?: string, principalId?: string) => Promise<void>;

    // Permission assignment actions
    listPermissionAssignments: (scopeId?: string, principalId?: string, principalType?: string) => Promise<PermissionAssignment[]>;
    createPermissionAssignment: (data: Omit<PermissionAssignment, 'id' | 'assigned_at' | 'assigned_by'>) => Promise<PermissionAssignment>;
    deletePermissionAssignment: (assignmentId: string, scopeId?: string, principalId?: string) => Promise<void>;
};

export const useAssignments = create<AssignmentStore>((set) => ({
    assignments: {},
    userAssignments: {},
    permissionAssignments: {},
    isLoading: false,
    error: null,

    list: async (scopeId: string) => {
        set({ isLoading: true, error: null });
        try {
            const payload: any = await http.get(`/mgmt/v1/assignments?scope_id=${encodeURIComponent(scopeId)}`);
            const data = Array.isArray(payload) ? payload : payload.data || [];
            set(state => ({
                assignments: { ...state.assignments, [scopeId]: data },
                isLoading: false,
            }));
            return data;
        } catch (err: any) {
            set({ error: err.message, isLoading: false });
            return [];
        }
    },

    listForPrincipal: async (principalId: string, principalType: 'user' | 'group' | 'spn') => {
        set({ isLoading: true, error: null });
        try {
            const payload: any = await http.get(
                `/mgmt/v1/assignments?principal_id=${encodeURIComponent(principalId)}&principal_type=${principalType}`
            );
            const data = Array.isArray(payload) ? payload : payload.data || [];
            set(state => ({
                userAssignments: { ...state.userAssignments, [principalId]: data },
                isLoading: false,
            }));
            return data;
        } catch (err: any) {
            set({ error: err.message, isLoading: false });
            return [];
        }
    },

    create: async (data) => {
        const { scope_id, principal_id } = data;
        try {
            const created = await http.post<RoleAssignment>('/mgmt/v1/assignments/roles', data);
            set(state => ({
                assignments: {
                    ...state.assignments,
                    [scope_id]: [...(state.assignments[scope_id] || []), created],
                },
                userAssignments: {
                    ...state.userAssignments,
                    [principal_id]: [...(state.userAssignments[principal_id] || []), created],
                },
            }));
            return created;
        } catch (err: any) {
            set({ error: err.message });
            throw err;
        }
    },

    createBulk: async (assignments: BulkAssignmentItem[]) => {
        try {
            const result = await http.post<BulkAssignmentResult>('/mgmt/v1/assignments/bulk', { assignments });
            // Merge succeeded assignments into local state by scope
            const byScope: Record<string, RoleAssignment[]> = {};
            for (const a of result.succeeded) {
                if (!byScope[a.scope_id]) byScope[a.scope_id] = [];
                byScope[a.scope_id].push(a);
            }
            set(state => {
                const updated: Record<string, RoleAssignment[]> = { ...state.assignments };
                for (const [scopeId, items] of Object.entries(byScope)) {
                    updated[scopeId] = [...(updated[scopeId] || []), ...items];
                }
                return { assignments: updated };
            });
            return result;
        } catch (err: any) {
            set({ error: err.message });
            throw err;
        }
    },

    delete: async (assignmentId: string, scopeId?: string, principalId?: string) => {
        try {
            await http.delete(`/mgmt/v1/assignments/roles/${assignmentId}`);
            set(state => {
                const newState: Partial<AssignmentStore> = {};
                if (scopeId) {
                    newState.assignments = {
                        ...state.assignments,
                        [scopeId]: (state.assignments[scopeId] || []).filter(a => a.id !== assignmentId),
                    };
                }
                if (principalId) {
                    newState.userAssignments = {
                        ...state.userAssignments,
                        [principalId]: (state.userAssignments[principalId] || []).filter(a => a.id !== assignmentId),
                    };
                }
                return newState as AssignmentStore;
            });
        } catch (err: any) {
            set({ error: err.message });
            throw err;
        }
    },

    listPermissionAssignments: async (scopeId?: string, principalId?: string, principalType?: string) => {
        set({ isLoading: true, error: null });
        try {
            const params = new URLSearchParams();
            if (scopeId) params.append('scope_id', scopeId);
            if (principalId) params.append('principal_id', principalId);
            if (principalType) params.append('principal_type', principalType);
            const query = params.toString() ? `?${params.toString()}` : '';
            const payload: any = await http.get(`/mgmt/v1/assignments/permissions${query}`);
            const data: PermissionAssignment[] = Array.isArray(payload) ? payload : payload.data || [];
            const key = scopeId || principalId || 'all';
            set(state => ({
                permissionAssignments: { ...state.permissionAssignments, [key]: data },
                isLoading: false,
            }));
            return data;
        } catch (err: any) {
            set({ error: err.message, isLoading: false });
            return [];
        }
    },

    createPermissionAssignment: async (data) => {
        try {
            const created = await http.post<PermissionAssignment>('/mgmt/v1/assignments/permissions', data);
            const key = data.scope_id || data.principal_id;
            set(state => ({
                permissionAssignments: {
                    ...state.permissionAssignments,
                    [key]: [...(state.permissionAssignments[key] || []), created],
                },
            }));
            return created;
        } catch (err: any) {
            set({ error: err.message });
            throw err;
        }
    },

    deletePermissionAssignment: async (assignmentId: string, scopeId?: string, principalId?: string) => {
        try {
            await http.delete(`/mgmt/v1/assignments/permissions/${assignmentId}`);
            set(state => {
                const key = scopeId || principalId;
                if (!key) return state;
                return {
                    permissionAssignments: {
                        ...state.permissionAssignments,
                        [key]: (state.permissionAssignments[key] || []).filter(a => a.id !== assignmentId),
                    },
                };
            });
        } catch (err: any) {
            set({ error: err.message });
            throw err;
        }
    },
}));
